import { createContext } from 'react';
import moment from 'moment';
import { ROUTE_META } from '../../RouteMeta';
import { getDate } from '../../Services/helpers/Paradigm/planHelper';

export const uuidContext = createContext('principal');

export const saveData = (uuid, data) => {
  sessionStorage.setItem(`${uuid}_PlanDesignSession`, JSON.stringify(data));
};

export const stepTrackerObj = (steps, active = '') => {
  const visibleSteps = steps.filter((step) => step.props.showTracker === true);
  const activeStepIndex =
    visibleSteps.findIndex((step) => step.props.name === active) + 1;
  return {
    visibleSteps,
    activeStepIndex,
    totalSteps: visibleSteps.length,
  };
};

export const isActive = (startAtStep, name, activeStep) =>
  (activeStep || startAtStep) === name;

export const checkValidityOnQuestionArray = (arrayOfQuestions = []) => {
  const isDirty = arrayOfQuestions.filter((q) => q.dirty);
  const hasError = arrayOfQuestions.filter((q) => q.hasError);

  // const invalid = arrayOfQuestions.filter((question) => {
  //   if (question.name !== 'advisorNpn') {
  //     return checkForErrors(question.name, question.value, arrayOfQuestions);
  //   }
  //   return false;
  // });
  // if (invalid) console.info('The following questions are blank:', invalid);

  let dirty = false;
  let error = true;
  if (isDirty.length === arrayOfQuestions.length) {
    dirty = true;
  }
  if (hasError.length === 0) {
    error = false;
  }
  // console.log(isDirty, arrayOfQuestions);

  return dirty && !error;
};

export const checkAdvisorCredentialVerification = (
  advisorCredentialVerification,
) => {
  switch (advisorCredentialVerification.brokerType) {
    case 'iarRia':
    case 'brokerDealer':
      return (
        advisorCredentialVerification.brokerEid !== '' &&
        advisorCredentialVerification.crd.value !== ''
      );
    case 'insuranceAgent':
      return (
        advisorCredentialVerification.insuranceAgentCompType === 'direct' ||
        (advisorCredentialVerification.insuranceAgent !== '' &&
          advisorCredentialVerification.insuranceAgentNpn !== '')
      );
    default:
      return false;
  }
};

export const checkErrorPlanEffectiveDate = (planEffectiveDate) => {
  const [threeWeeksMonth, threeWeeksDay, threeWeeksYear] = new Date(
    Date.now() + 1814400000,
  )
    .toLocaleDateString('en-us')
    .split('/');
  const dateThreeWeeks = {
    value: getDate(threeWeeksYear, threeWeeksMonth, threeWeeksDay),
    valueFormatted: 'three weeks from now',
  };
  return moment(planEffectiveDate.value).isBefore(dateThreeWeeks.value);
};

export const multiQuestionNextDisabled = (primaryQuestion, secondaryQuestion) =>
  primaryQuestion.value === 'yes' && !secondaryQuestion.value;

export const isEligible = (value) => parseInt(value, 10) < 100;

export const numberToCurrency = (number, fraction = 0) => {
  if (number) {
    return `$${number.toLocaleString(
      ('us-US',
      {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: fraction,
        maximumFractionDigits: fraction,
      }),
    )}`;
  }
  return '$0';
};

export const hasFinancialProfessional = (value) => value;

export const restrictedState = (code) => {
  switch (code) {
    case 'FL':
      return true;
    case 'TX':
      return true;
    case 'GA':
      return true;
    default:
      return false;
  }
};

export const restrictedStateInsuranceAgent = (code) => {
  switch (code) {
    case 'select':
      return false;
    case 'AK':
      return false;
    case 'AZ':
      return false;
    case 'AR':
      return false;
    case 'CA':
      return false;
    case 'FL':
      return false;
    case 'HI':
      return false;
    case 'ID':
      return false;
    case 'IL':
      return false;
    case 'IN':
      return false;
    case 'IA':
      return false;
    case 'KS':
      return false;
    case 'LA':
      return false;
    case 'MI':
      return false;
    case 'MS':
      return false;
    case 'MO':
      return false;
    case 'NJ':
      return false;
    case 'OH':
      return false;
    case 'OR':
      return false;
    case 'TN':
      return false;
    case 'TX':
      return false;
    case 'VA':
      return false;
    case 'WA':
      return false;
    case 'WV':
      return false;
    case 'WY':
      return false;
    default:
      return true;
  }
};

export const employerCanPurchasePlan = (
  code,
  hasFp,
  hasTpa,
  advisorInfo,
  tpaInfo,
  employerInfo,
  formData = {},
) => {
  const { advisorCredentialVerification } =
    hasFp || hasFp.value ? formData.planDesign.advisors[0] : {};
  if (hasTpa) {
    if (!checkValidityOnQuestionArray(tpaInfo)) {
      return 'employer-share';
    }
  }
  if (restrictedState(code)) {
    if (
      checkAdvisorCredentialVerification(advisorCredentialVerification) &&
      checkValidityOnQuestionArray(advisorInfo) &&
      checkValidityOnQuestionArray(employerInfo)
    ) {
      return 'employer';
    }
    return 'employer-share';
  }
  if (hasFinancialProfessional(hasFp)) {
    const { brokerType } =
      formData.planDesign.advisors[0].advisorCredentialVerification;
    if (
      restrictedStateInsuranceAgent(code) &&
      brokerType === 'insuranceAgent'
    ) {
      return 'advisor-error';
    }
    if (
      checkAdvisorCredentialVerification(advisorCredentialVerification) &&
      checkValidityOnQuestionArray(advisorInfo) &&
      checkValidityOnQuestionArray(employerInfo)
    ) {
      return 'employer';
    }
    return 'employer-share';
  }
  if (checkValidityOnQuestionArray(employerInfo)) {
    return 'employer';
  }
  return 'employer-error';
};

const advisorNextAction = (userRole, advisorInfo, formData, isValid) => {
  const isFpValid =
    checkValidityOnQuestionArray(advisorInfo) &&
    checkAdvisorCredentialVerification(
      formData.planDesign.advisors[0].advisorCredentialVerification,
    );
  const shareWith = whoToShareWith(
    userRole,
    formData.planDesign.questions.hasFp.value,
    formData.planDesign.questions.hasTpa.value,
    isFpValid,
    isValid,
    formData.planOriginator.role,
  );
  if (
    !checkValidityOnQuestionArray(advisorInfo) ||
    !checkAdvisorCredentialVerification(
      formData.planDesign.advisors[0].advisorCredentialVerification,
    )
  )
    return 'advisor-error';

  if (shareWith === 'advisorToEitherParty') return 'advisor-to-either';
  if (shareWith === 'advisorToTpa') {
    if (formData.planOriginator.role === 'tpa') {
      return 'advisor-save-only';
    }
  }
  if (shareWith === 'advisorToEmployer') {
    if (formData.planOriginator.role === 'employer') {
      return 'advisor-save-only';
    }
  }

  return 'advisor';
};

const tpaNextAction = (userRole, advisorInfo, tpaInfo, formData, isValid) => {
  const isFpValid =
    checkValidityOnQuestionArray(advisorInfo) &&
    checkAdvisorCredentialVerification(
      formData.planDesign.advisors[0].advisorCredentialVerification,
    );
  const shareWith = whoToShareWith(
    userRole,
    formData.planDesign.questions.hasFp.value,
    formData.planDesign.questions.hasTpa.value,
    isFpValid,
    isValid,
    formData.planOriginator.role,
  );
  if (!checkValidityOnQuestionArray(tpaInfo)) return 'tpa-error';
  if (shareWith === 'tpaToAdvisor') {
    if (formData.planOriginator.role === 'advisor') {
      return 'tpa-save-only';
    }
  }

  if (shareWith === 'tpaToEitherParty') return 'tpa-to-either';
  if (shareWith === 'tpaToEmployer') {
    if (formData.planOriginator.role === 'employer') {
      return 'tpa';
    }
    if (formData.planOriginator.role === 'advisor') {
      return 'tpa-save-only';
    }
  }

  return 'tpa';
};

export const validUser = (
  userRole,
  employerStateCode,
  hasFp,
  hasTpa,
  advisorInfo,
  tpaInfo,
  employerInfo,
  formData,
  isValid,
) => {
  switch (userRole) {
    case 'employer':
    case 'psivet':
    case 'onedigital':
      return employerCanPurchasePlan(
        employerStateCode,
        hasFp,
        hasTpa,
        advisorInfo,
        tpaInfo,
        employerInfo,
        formData,
      );
    case 'advisor':
      return advisorNextAction(userRole, advisorInfo, formData, isValid);
    case 'tpa':
      return tpaNextAction(userRole, advisorInfo, tpaInfo, formData, isValid);
    default:
      return 'anonymous';
  }
};

export const errorStep = (questionsArry) => {
  const error = questionsArry.find((q) => q.hasError);
  if (error) {
    return error.stepName;
  }
  return 'roleSelection';
};

export const pushGoogleDataLayer = (obj) => {
  if (typeof window !== 'undefined') {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      ...obj,
    });
  }
};

export const checkNpnValue = (npnValue, questions) => {
  switch (true) {
    case shouldRequireNpn(questions):
      return !(npnValue.length >= 4 && npnValue.length <= 10);
    case npnValue.length > 0:
      return !(npnValue.length >= 4 && npnValue.length <= 10);
    default:
      return npnValue.length < 0;
  }
};

export const shouldRequireNpn = () => true;

function isEmptyOrSpaces(str) {
  if (typeof str === 'string') return str === '' || str.match(/^\s*$/) !== null;
  return false;
}

export const checkForErrors = (questionId, value, questions) => {
  switch (questionId) {
    case 'numberOfEmployees':
      return !isEligible(value);
    case 'advisorFee':
    case 'fixedAmount':
      return value === '' || value > 5000;
    case 'tpaDistributionFee':
    case 'tpaHardshipFee':
    case 'tpaLoanMaintenanceFee':
    case 'tpaLoanOriginationFee':
    case 'tpaLoanReamortizationFee':
    case 'tpaRmdFee':
    case 'tpaQdroFee':
      return value === '' || value > 500;
    case 'bpsAmount':
      return value === '' || value > 250;
    case 'advisorNpn':
    case 'insuranceAgentNpn':
      return checkNpnValue(value, questions);
    case 'advisorFirmBroker':
      return Object.entries(value).length === 0;
    case 'employerStateCode':
      return value === 'select';
    case 'advisorPhone':
    case 'companyPhone':
    case 'tpaPhone':
      return value.length < 10;
    case 'advisorEmail':
    case 'sponsorEmail':
    case 'tpaEmail':
      return !/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
        value.toLowerCase(),
      );
    case 'advisorZipCode':
    case 'tpaZipCode':
      return !/^\d{5}$/.test(value);
    case 'floridaLicenseNumber':
      return !/^[a-zA-Z]\d{4,6}$/.test(value);
    case 'sponsorTitle':
      return false;
    case 'employerIdentificationNumber':
      return !/^[a-zA-Z0-9-]{9,10}$/.test(value);
    case 'sponsorEmployerIdentificationNumber':
      return !/^\d{9}$/.test(value);
    case 'advisorStateCode':
      return value === 'select' || value === 'Select';
    default:
      return isEmptyOrSpaces(value);
  }
};

export const roleSpecificPath = (userRole, rootRoute, path) => {
  if (
    userRole === ROUTE_META.psivet.role ||
    rootRoute === ROUTE_META.psivet.route
  ) {
    return `${ROUTE_META.psivet.route}${path}`;
  }
  if (
    userRole === ROUTE_META.onedigital.role ||
    rootRoute === ROUTE_META.onedigital.route
  ) {
    return `${ROUTE_META.onedigital.route}${path}`;
  }
  if (rootRoute === ROUTE_META.tpa.route) {
    return `${ROUTE_META.tpa.route}${path}`;
  }
  return path;
};

export const whoToShareWith = (
  userRole,
  hasFp,
  hasTpa,
  validFp,
  isValid,
  creator,
) => {
  const isCreator = normalizeCreator() === userRole;

  function upcase(string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }
  function normalizeCreator() {
    switch (creator) {
      case 'psivet':
      case 'employer':
        return 'employer';
      case 'advisor':
        return 'advisor';
      case 'tpa':
        return 'tpa';
      default:
        return 'Anonymous';
    }
  }

  switch (userRole) {
    case 'psivet':
    case 'employer':
      if (hasFp && validFp && hasTpa && isValid) return 'employerToAnonymous';
      if (hasFp && hasTpa)
        return isCreator
          ? 'employerToFp'
          : `employerTo${upcase(normalizeCreator())}`;
      if (!hasFp && hasTpa && isValid) return 'employerToAnonymous';
      if (!hasFp && hasTpa) return 'employerToTpa';
      if (hasFp && validFp && !hasTpa) return 'employerToAnonymous';
      if (hasFp && !hasTpa) return 'employerToAdvisor';
      return `employerToAnonymous`;
    case 'advisor':
      if (!isCreator && hasTpa && isValid) return 'advisorToEitherParty';
      if (hasTpa && isValid) return 'advisorToEmployer';
      if (hasTpa && !isValid) return 'advisorToTpa';
      return 'advisorToEmployer';
    case 'tpa':
      if (hasFp) {
        if (!isCreator) return 'tpaToEitherParty';
        if (isValid && validFp) return 'tpaToEitherParty';
        if (isCreator) return 'tpaToAdvisor';
      }
      return 'tpaToEmployer';
    default:
      return `employerToAnonymous`;
  }
};

export const isShareDisabled = (
  userRole,
  hasFp,
  hasTpa,
  advisorInfo,
  isValid,
  planOriginator,
) => {
  const shareWith = whoToShareWith(
    userRole,
    hasFp.value,
    hasTpa.value,
    checkValidityOnQuestionArray(advisorInfo),
    isValid,
    planOriginator.role,
  );

  if (shareWith === 'tpaToEmployer') {
    if (planOriginator.role === 'employer') {
      return true;
    }
  }
  if (shareWith === 'tpaToAdvisor') {
    if (planOriginator.role === 'advisor') {
      return true;
    }
  }
  if (shareWith === 'advisorToTpa') {
    if (planOriginator.role === 'tpa') {
      return true;
    }
  }
  if (shareWith === 'advisorToEmployer') {
    if (planOriginator.role === 'employer') {
      return true;
    }
  }
  return false;
};

export const onSaveToast = (hashId, userRole, planOriginator, action) => {
  if (!hashId) {
    return 'You’ll receive an email with a link to your plan.';
  }
  if (userRole === 'advisor' && planOriginator.role === 'employer') {
    return `You've successfully shared this plan.`;
  }
  if (
    userRole !== planOriginator.role &&
    userRole !== 'advisor' &&
    planOriginator.role !== 'employer'
  ) {
    return `We’ll notify ${planOriginator.name} by email that the plan has been updated.`;
  }
  return `Your proposal has been successfully ${action}.`;
};
